/**
 * 缓存数据结构接口
 */
export interface ICacheData {
  gameCode?: string;
  userId?: string;
  step?: any;
  modelList?: IModelItem[];
  activeId?: string;
  errorContent?: any;
  timestamp: number;
}

/**
 * 模型项接口
 */
export interface IModelItem {
  uuid?: string;
  game_code: string;
  name: string;
  yaml_context: string;
  database_uuid: string;
  database_name: string;
  table_id: string;
  table_name: string;
  isValid?: boolean;
  deployment_stage?: string;
}

/**
 * 缓存管理器接口
 */
export interface ICacheManager {
  save(data: Partial<ICacheData>): void;
  load(): ICacheData | null;
  clear(): void;
  isValid(): boolean;
  getCacheKey(): string;
  isExpired(): boolean;
}

/**
 * 缓存配置
 */
const CACHE_CONFIG = {
  EXPIRY_HOURS: 24, // 缓存过期时间（小时）
  STORAGE_KEY_PREFIX: '_dbl_model_create_cache',
} as const;

/**
 * 缓存管理器实现
 */
export class CacheManager implements ICacheManager {
  private cacheKey: string;

  constructor(cahceKey?: string) {
    this.cacheKey = this.getCacheKey(cahceKey);
  }

  /**
   * 获取缓存键名
   */
  getCacheKey(cahceKey?: string): string {
    return cahceKey || `${CACHE_CONFIG.STORAGE_KEY_PREFIX}`;
  }

  /**
   * 保存缓存数据
   */
  save(data: Partial<ICacheData>): void {
    try {
      const existingData = this.load() || {};
      const newData: ICacheData = {
        timestamp: Date.now(),
        ...existingData,
        ...data,
      };

      // 保存到localStorage
      localStorage.setItem(this.cacheKey, JSON.stringify(newData));
    } catch (error) {
      console.error('缓存保存失败:', error);
      // 尝试清理损坏的缓存
      this.clear();
    }
  }

  /**
   * 加载缓存数据
   */
  load(): ICacheData | null {
    try {
      // 优先从localStorage加载
      const data = localStorage.getItem(this.cacheKey);
      if (!data) {
        return null;
      }

      const parsedData: ICacheData = JSON.parse(data);

      // 验证缓存数据
      if (!this.validateCacheData(parsedData)) {
        console.warn('缓存数据验证失败，清理缓存');
        this.clear();
        return null;
      }

      // 检查是否过期
      if (this.isExpired()) {
        console.warn('缓存数据过期，清理缓存');
        this.clear();
        return null;
      }

      return parsedData;
    } catch (error) {
      console.error('缓存加载失败:', error);
      this.clear();
      return null;
    }
  }

  /**
   * 清理缓存
   */
  clear(): void {
    try {
      localStorage.removeItem(this.cacheKey);
      console.log('缓存清理成功:', this.cacheKey);
    } catch (error) {
      console.error('缓存清理失败:', error);
    }
  }

  /**
   * 验证缓存数据是否有效
   */
  isValid(): boolean {
    const data = localStorage.getItem(this.cacheKey);
    return data !== null && !this.isExpired();
  }

  /**
   * 检查缓存是否过期
   */
  isExpired(): boolean {
    const data = localStorage.getItem(this.cacheKey);
    if (!data) return true;
    const parsedData: ICacheData = JSON.parse(data);
    const expiryTime = parsedData?.timestamp + (CACHE_CONFIG.EXPIRY_HOURS * 60 * 60 * 1000);
    return Date.now() > expiryTime;
  }

  /**
   * 验证缓存数据结构
   */
  private validateCacheData(data: any): data is ICacheData {
    return (
      data &&
      typeof data === 'object' &&
      Array.isArray(data?.modelList) &&
      typeof data?.userId === 'string' &&
      typeof data?.gameCode === 'string' &&
      typeof data?.timestamp === 'number'
    );
  }
}

/**
 * 创建缓存管理器实例
 */
export const createCacheManager = (cahceKey?: string): CacheManager => new CacheManager(cahceKey);
